import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Clock,
  Check,
  ChevronRight,
  ChevronLeft,
  Car,
  MapPin,
  User,
  CalendarDays,
  CreditCard,
  Loader2,
  Gift,
  Sparkles,
} from "lucide-react";
import { api } from "@/lib/api";
import { SIZES, priceFor, hasSizePricing } from "@/lib/pricing";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PhotoUpload from "@/components/PhotoUpload";
import ExtrasToggles from "@/components/ExtrasToggles";
import { Calendar } from "@/components/ui/calendar";

const STEPS = [
  { key: "service", label: "Service", icon: Clock },
  { key: "vehicle", label: "Vehicle", icon: Car },
  { key: "contact", label: "Contact", icon: User },
  { key: "schedule", label: "Schedule", icon: CalendarDays },
  { key: "pay", label: "Deposit", icon: CreditCard },
];

function todayPlus(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function fmtDate(iso) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
}

function isoToDate(iso) {
  return new Date(iso + "T00:00:00");
}

function dateToIso(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function extractPostcode(address) {
  const m = (address || "").match(/\b(\d{4})\b/);
  return m ? m[1] : "";
}

export default function Booking() {
  const [search] = useSearchParams();
  const navigate = useNavigate();

  const [services, setServices] = useState([]);
  const [addons, setAddons] = useState([]);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [prereqAccepted, setPrereqAccepted] = useState(false);
  const [waitlisted, setWaitlisted] = useState(null);
  const [requested, setRequested] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [recurringPlan, setRecurringPlan] = useState("");
  const RECURRING_OPTS = [
    { key: "monthly-exterior", label: "Monthly Exterior Upkeep", cadence: "every 4 weeks" },
    { key: "quarterly-full", label: "Quarterly Full Detail", cadence: "every 3 months" },
  ];
  const [config, setConfig] = useState({ deposit_amount: 25, currency: "AUD" });
  const [stepIdx, setStepIdx] = useState(0);

  const [serviceId, setServiceId] = useState(search.get("service") || "");
  const [vehicle, setVehicle] = useState({ year: "", make: "", model: "", color: "", size: "sedan" });
  const [customer, setCustomer] = useState({ name: "", email: "", phone: "", address: "", postcode: "" });
  const [photos, setPhotos] = useState([]);
  const [notes, setNotes] = useState("");
  const [referralCode, setReferralCode] = useState(search.get("ref") || "");

  const [dateSel, setDateSel] = useState(todayPlus(1));
  const minDate = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 1);
    return d;
  }, []);
  const [timeSel, setTimeSel] = useState("");
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);

  const [areaCheck, setAreaCheck] = useState(null); // {within_zone, travel_surcharge, message, distance_km, place}
  const [customerInfo, setCustomerInfo] = useState(null); // {paid_bookings, next_loyalty_credit, referral_code}
  const [refStatus, setRefStatus] = useState(null); // {valid, discount, reason}
  const [refChecking, setRefChecking] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const service = useMemo(() => services.find((s) => s.id === serviceId), [services, serviceId]);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/addons").then((r) => setAddons(r.data)).catch(() => {});
    api.get("/config/public").then((r) => setConfig(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!dateSel) return;
    setSlotsLoading(true);
    api
      .get(`/availability`, { params: { date: dateSel } })
      .then((r) => setSlots(r.data.slots || []))
      .finally(() => setSlotsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateSel]);

  // Postcode detection from address
  useEffect(() => {
    const pc = customer.postcode || extractPostcode(customer.address);
    if (!pc || pc.length !== 4) {
      setAreaCheck(null);
      return;
    }
    const t = setTimeout(() => {
      api.get(`/service-area/check`, { params: { postcode: pc } }).then((r) => setAreaCheck(r.data));
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer.address, customer.postcode]);

  // Customer loyalty info by email
  useEffect(() => {
    const email = customer.email.trim().toLowerCase();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setCustomerInfo(null);
      return;
    }
    const t = setTimeout(() => {
      api.get(`/customer/info`, { params: { email } }).then((r) => setCustomerInfo(r.data));
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer.email]);

  // Referral code live validation
  useEffect(() => {
    const email = customer.email.trim().toLowerCase();
    const code = referralCode.trim();
    if (!code || !email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setRefStatus(null);
      setRefChecking(false);
      return;
    }
    setRefChecking(true);
    setRefStatus(null);
    const t = setTimeout(async () => {
      try {
        const res = await api.get(`/referral/validate`, { params: { code, email } });
        setRefStatus({ valid: res.data.valid, discount: res.data.discount, reason: res.data.reason });
      } catch {
        setRefStatus(null);
      } finally {
        setRefChecking(false);
      }
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referralCode, customer.email]);

  const travelFee = areaCheck?.travel_surcharge || 0;
  const beyondCap = !!areaCheck?.beyond_cap;
  const servicePrice = priceFor(service, vehicle.size);
  const needsPrereq = !!service?.prerequisite;
  const chosenAddons = addons.filter((a) => selectedAddons.includes(a.id));
  const addonsTotal = chosenAddons.reduce((s, a) => s + priceFor(a, vehicle.size), 0);
  const toggleAddon = (id) =>
    setSelectedAddons((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const loyaltyCredit = customerInfo?.next_loyalty_credit || 0;
  const referralDiscount =
    refStatus?.valid && customerInfo?.paid_bookings === 0 ? refStatus.discount : 0;

  const totalDue =
    servicePrice + addonsTotal + travelFee - (config.deposit_amount || 0) - loyaltyCredit - referralDiscount;
  const balanceOnCompletion = Math.max(0, totalDue);

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const emailOk = EMAIL_RE.test(customer.email.trim());

  const canNext = () => {
    if (stepIdx === 0) return !!serviceId && (!needsPrereq || prereqAccepted);
    if (stepIdx === 1) return vehicle.year.trim() && vehicle.make.trim() && vehicle.model.trim();
    if (stepIdx === 2) return customer.name.trim() && emailOk && customer.phone.trim() && customer.address.trim();
    if (stepIdx === 3) return !!dateSel && !!timeSel;
    return true;
  };

  const next = () => {
    setError("");
    if (!canNext()) {
      if (stepIdx === 0 && serviceId && needsPrereq && !prereqAccepted) setError("Please confirm the garage requirement to continue.");
      else if (stepIdx === 0 && !serviceId) setError("Please select a package to continue.");
      else if (stepIdx === 2 && customer.email && !emailOk) setError("Please enter a valid email address (e.g. name@example.com).");
      else setError("Please complete the required fields.");
      return;
    }
    setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
  };

  const back = () => setStepIdx((i) => Math.max(i - 1, 0));

  const submitBooking = async () => {
    setError("");
    if (!termsAccepted) {
      setError("Please accept the Terms & Conditions to continue.");
      return;
    }
    setSubmitting(true);
    try {
      const pc = customer.postcode || extractPostcode(customer.address);
      const { data: booking } = await api.post("/bookings", {
        service_id: serviceId,
        customer: {
          name: customer.name.trim(),
          email: customer.email.trim().toLowerCase(),
          phone: customer.phone.trim(),
          address: customer.address.trim(),
          postcode: pc,
        },
        vehicle: { ...vehicle, year: String(vehicle.year).trim(), make: vehicle.make.trim(), model: vehicle.model.trim() },
        scheduled_date: dateSel,
        scheduled_time: timeSel,
        notes,
        photo_ids: photos.map((p) => p.id),
        addon_ids: selectedAddons,
        prerequisite_accepted: prereqAccepted,
        terms_accepted: termsAccepted,
        recurring_plan: recurringPlan || null,
        referral_code: referralCode.trim(),
      });
      sessionStorage.setItem("cj_last_booking_id", booking.id);
      sessionStorage.setItem("cj_last_referral_code", booking.referral_code_issued || "");
      if (booking.status === "waitlisted") {
        setWaitlisted(booking);
      } else {
        setRequested(booking);
      }
      setSubmitting(false);
    } catch (e) {
      setError(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b0f17]">
      <Navbar />

      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10" data-testid="booking-wizard">
        <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#ff6b00]">Book your detail</div>
        <h1 className="font-display mt-2 text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight text-slate-100">
          Lock your slot in 60 seconds.
        </h1>

        {/* Steps bar */}
        <div className="mt-8 overflow-x-auto">
          <div className="flex items-center gap-3 min-w-max">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const active = i === stepIdx;
              const done = i < stepIdx;
              return (
                <div key={s.key} className="flex items-center gap-3">
                  <div
                    data-testid={`step-indicator-${s.key}`}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full border transition ${
                      active
                        ? "bg-[#ff6b00] text-[#0b0f17] border-[#ff6b00]"
                        : done
                        ? "bg-[#10b981]/10 text-[#10b981] border-[#10b981]/40"
                        : "bg-[#121824] text-slate-400 border-white/10"
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="font-display uppercase font-bold text-xs tracking-wider">
                      {String(i + 1).padStart(2, "0")} · {s.label}
                    </span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className={`h-px w-6 ${i < stepIdx ? "bg-[#10b981]" : "bg-white/10"}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-10 grid lg:grid-cols-3 gap-8">
          {/* MAIN FORM */}
          <div className="lg:col-span-2">
            <div className="cj-card p-6 sm:p-8 cj-fade-up" key={stepIdx}>
              {stepIdx === 0 && (
                <div data-testid="step-service">
                  <h2 className="font-display uppercase text-2xl font-extrabold text-slate-100">Pick a package</h2>
                  <p className="text-sm text-slate-400 mt-1">
                    Flat ${config.deposit_amount} deposit — paid only after CJ confirms your booking.
                  </p>
                  <p className="text-xs text-slate-500 mt-1" data-testid="price-varies-note">
                    Prices shown are a guide — final pricing may vary based on your vehicle's condition.
                  </p>

                  <Label className="mt-6 block">Choose your package</Label>
                  <div className="mt-2 grid gap-3">
                    {services.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => { setServiceId(s.id); setPrereqAccepted(false); }}
                        data-testid={`select-service-${s.slug}`}
                        className={`text-left p-4 rounded-xl border transition flex items-center gap-4 ${
                          serviceId === s.id
                            ? "border-[#ff6b00] bg-[#ff6b00]/5"
                            : "border-white/10 hover:border-white/20 bg-[#0f1522]"
                        }`}
                      >
                        {s.image_url && (
                          <img src={s.image_url} alt="" className="h-16 w-16 rounded-lg object-cover shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="font-display uppercase font-bold text-lg text-slate-100">{s.name}</div>
                            {s.slug === "full-detail" && (
                              <span data-testid="most-popular-badge" className="cj-pill bg-[#ff6b00]/15 text-[#ff6b00] border border-[#ff6b00]/40 text-[10px]">★ Most popular</span>
                            )}
                          </div>
                          <div className="text-xs text-slate-400 mt-0.5 line-clamp-1">{s.description}</div>
                          <div className="mt-1 flex items-center gap-3 flex-wrap">
                            <span className="cj-pill bg-white/5 text-slate-300 border border-white/10">
                              <Clock className="h-3 w-3" /> {s.duration_minutes}m
                            </span>
                            {s.quote_only ? (
                              <>
                                <span className="cj-pill bg-cyan-500/10 text-cyan-300 border border-cyan-500/30" data-testid={`quote-only-${s.slug}`}>Quote only</span>
                                <span className="font-mono text-xs text-slate-500">from</span>
                              </>
                            ) : (
                              <span className="font-mono text-xs text-slate-500">{hasSizePricing(s) ? SIZES.find((z) => z.v === vehicle.size)?.l : "flat"}</span>
                            )}
                            <span className="font-display font-extrabold text-xl text-[#ff6b00]" data-testid={`service-price-${s.slug}`}>${priceFor(s, vehicle.size).toFixed(0)}</span>
                          </div>
                        </div>
                        {serviceId === s.id && (
                          <div className="h-7 w-7 rounded-full bg-[#ff6b00] flex items-center justify-center">
                            <Check className="h-4 w-4 text-[#0b0f17]" strokeWidth={3} />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>

                  <div className="mt-6">
                    <Label>Vehicle size — prices update live</Label>
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {SIZES.map((s) => (
                        <button
                          key={s.v}
                          type="button"
                          onClick={() => setVehicle({ ...vehicle, size: s.v })}
                          data-testid={`vehicle-size-${s.v}`}
                          className={`px-3 py-2.5 rounded-lg text-left border transition ${
                            vehicle.size === s.v
                              ? "bg-[#ff6b00] text-[#0b0f17] border-[#ff6b00]"
                              : "bg-[#0f1522] text-slate-300 border-white/10 hover:border-white/20"
                          }`}
                        >
                          <div className="font-display uppercase font-bold text-xs tracking-wider">{s.l}</div>
                          <div className={`text-[10px] mt-0.5 ${vehicle.size === s.v ? "text-[#0b0f17]/70" : "text-slate-500"}`}>{s.hint}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {service?.quote_only && (
                    <div className="mt-4 p-4 rounded-xl border border-cyan-500/30 bg-cyan-500/5 text-sm text-cyan-100" data-testid="quote-only-notice">
                      <span className="font-display uppercase font-bold text-cyan-300">Priced by quote.</span> {service.name} starts at ${servicePrice.toFixed(0)}. Your $
                      {config.deposit_amount} deposit books the inspection; CJ confirms the final price with you before any work starts.
                    </div>
                  )}

                  {needsPrereq && (
                    <label
                      className={`mt-4 p-4 rounded-xl border flex items-start gap-3 cursor-pointer transition ${
                        prereqAccepted ? "border-emerald-500/40 bg-emerald-500/5" : "border-amber-500/40 bg-amber-500/5"
                      }`}
                      data-testid="prerequisite-box"
                    >
                      <input
                        type="checkbox"
                        checked={prereqAccepted}
                        onChange={(e) => setPrereqAccepted(e.target.checked)}
                        data-testid="prerequisite-checkbox"
                        className="mt-1 h-4 w-4 accent-[#ff6b00]"
                      />
                      <div className="text-sm">
                        <div className={`font-display uppercase font-bold ${prereqAccepted ? "text-emerald-300" : "text-amber-300"}`}>Required for {service.name}</div>
                        <div className="text-slate-300 mt-1">{service.prerequisite}</div>
                        <div className="text-xs text-slate-500 mt-1">Tick to confirm you can provide this on the day.</div>
                      </div>
                    </label>
                  )}

                  {addons.length > 0 && (
                    <div className="mt-8" data-testid="addons-section">
                      <div className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-[#00f0ff]" />
                        <Label>Boost your detail — optional add-ons</Label>
                      </div>
                      <div className="mt-3 grid sm:grid-cols-3 gap-3">
                        {addons.map((a) => {
                          const on = selectedAddons.includes(a.id);
                          return (
                            <button
                              key={a.id}
                              type="button"
                              onClick={() => toggleAddon(a.id)}
                              data-testid={`addon-toggle-${a.slug}`}
                              aria-pressed={on}
                              className={`text-left p-4 rounded-xl border transition ${
                                on ? "border-[#00f0ff] bg-[#00f0ff]/5" : "border-white/10 hover:border-white/20 bg-[#0f1522]"
                              }`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="font-display uppercase font-bold text-slate-100 leading-tight">{a.name}</div>
                                <div className={`h-5 w-5 rounded-md border flex items-center justify-center shrink-0 ${on ? "bg-[#00f0ff] border-[#00f0ff]" : "border-white/20"}`}>
                                  {on && <Check className="h-3.5 w-3.5 text-[#0b0f17]" strokeWidth={3} />}
                                </div>
                              </div>
                              <div className="text-xs text-slate-400 mt-1 line-clamp-2">{a.description}</div>
                              <div className="mt-2 flex items-center justify-between">
                                <span className="font-mono text-[10px] text-slate-500">+{a.duration_minutes}m{a.category ? ` · ${a.category}` : ""}</span>
                                <span className="font-display font-extrabold text-lg text-[#00f0ff]" data-testid={`addon-price-${a.slug}`}>+${priceFor(a, vehicle.size).toFixed(0)}</span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {stepIdx === 1 && (
                <div data-testid="step-vehicle">
                  <h2 className="font-display uppercase text-2xl font-extrabold text-slate-100">Tell us about the ride</h2>
                  <p className="text-sm text-slate-400 mt-1">
                    Add a few photos so we show up with the right pads, pH & protection.
                  </p>
                  <div className="mt-6 grid sm:grid-cols-2 gap-4">
                    <Field label="Year" testid="vehicle-year-input" value={vehicle.year} onChange={(v) => setVehicle({ ...vehicle, year: v })} placeholder="2022" />
                    <Field label="Make" testid="vehicle-make-input" value={vehicle.make} onChange={(v) => setVehicle({ ...vehicle, make: v })} placeholder="Toyota" />
                    <Field label="Model" testid="vehicle-model-input" value={vehicle.model} onChange={(v) => setVehicle({ ...vehicle, model: v })} placeholder="Camry" />
                    <Field label="Color (optional)" testid="vehicle-color-input" value={vehicle.color} onChange={(v) => setVehicle({ ...vehicle, color: v })} placeholder="Pearl White" />
                  </div>
                  <div className="mt-4">
                    <Label>Vehicle size</Label>
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {SIZES.map((s) => (
                        <button
                          key={s.v}
                          type="button"
                          onClick={() => setVehicle({ ...vehicle, size: s.v })}
                          data-testid={`vehicle-size-step2-${s.v}`}
                          className={`px-3 py-2 rounded-lg text-xs font-display uppercase font-bold tracking-wider border transition ${
                            vehicle.size === s.v
                              ? "bg-[#ff6b00] text-[#0b0f17] border-[#ff6b00]"
                              : "bg-[#0f1522] text-slate-300 border-white/10 hover:border-white/20"
                          }`}
                        >
                          {s.l}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="mt-6">
                    <Label>Photos of your car</Label>
                    <div className="mt-2">
                      <PhotoUpload photos={photos} setPhotos={setPhotos} />
                    </div>
                  </div>
                </div>
              )}

              {stepIdx === 2 && (
                <div data-testid="step-contact">
                  <h2 className="font-display uppercase text-2xl font-extrabold text-slate-100">Where should we come?</h2>
                  <p className="text-sm text-slate-400 mt-1">We detail at your home, office or hotel.</p>
                  <div className="mt-6 grid sm:grid-cols-2 gap-4">
                    <Field label="Full Name" testid="customer-name-input" value={customer.name} onChange={(v) => setCustomer({ ...customer, name: v })} placeholder="Chris Johnson" />
                    <Field label="Email" testid="customer-email-input" type="email" value={customer.email} onChange={(v) => setCustomer({ ...customer, email: v })} placeholder="chris@example.com" />
                    <Field label="Phone" testid="customer-phone-input" value={customer.phone} onChange={(v) => setCustomer({ ...customer, phone: v })} placeholder="04xx xxx xxx" />
                    <Field label="Service Address" testid="customer-address-input" value={customer.address} onChange={(v) => setCustomer({ ...customer, address: v, postcode: extractPostcode(v) })} placeholder="Street, Suburb VIC 3977" />
                  </div>
                  <div className="mt-4 grid sm:grid-cols-2 gap-4">
                    <Field
                      label="Postcode"
                      testid="customer-postcode-input"
                      value={customer.postcode}
                      onChange={(v) => setCustomer({ ...customer, postcode: v.replace(/\D/g, "").slice(0, 4) })}
                      placeholder="3977"
                    />
                    <Field label="Referral code (optional)" testid="customer-referral-input" value={referralCode} onChange={setReferralCode} placeholder="Save $10 with a friend's code" />
                  </div>

                  {areaCheck && (
                    <div
                      className={`mt-4 p-4 rounded-xl border flex items-start gap-3 ${
                        areaCheck.within_zone
                          ? "bg-emerald-500/5 border-emerald-500/30"
                          : beyondCap
                          ? "bg-purple-500/5 border-purple-500/30"
                          : "bg-amber-500/5 border-amber-500/30"
                      }`}
                      data-testid="service-area-indicator"
                    >
                      <MapPin
                        className={`h-5 w-5 shrink-0 mt-0.5 ${
                          areaCheck.within_zone ? "text-emerald-400" : beyondCap ? "text-purple-300" : "text-amber-400"
                        }`}
                      />
                      <div className="flex-1">
                        <div
                          className={`font-display uppercase font-bold text-sm ${
                            areaCheck.within_zone ? "text-emerald-300" : beyondCap ? "text-purple-200" : "text-amber-300"
                          }`}
                        >
                          {areaCheck.within_zone ? "In service zone" : beyondCap ? "Waitlist only" : "Travel fee applies"}
                        </div>
                        <div className="text-xs text-slate-300 mt-1">
                          {areaCheck.place
                            ? `${areaCheck.place}, ${areaCheck.state} · ${areaCheck.distance_km} km from Skye`
                            : "Postcode not recognised"}
                        </div>
                        {beyondCap && (
                          <div className="text-xs text-purple-200 mt-1" data-testid="waitlist-notice">
                            That's beyond our {areaCheck.max_travel_km} km range. You can still send a request — no deposit is taken and CJ will confirm by email.
                          </div>
                        )}
                        {!areaCheck.within_zone && !beyondCap && areaCheck.valid && (
                          <div className="text-xs text-amber-300 mt-1 font-mono">
                            +${areaCheck.travel_surcharge.toFixed(0)} travel surcharge
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {customerInfo?.paid_bookings > 0 && (
                    <div className="mt-3 p-4 rounded-xl border border-cyan-500/30 bg-cyan-500/5 flex items-center gap-3" data-testid="loyalty-indicator">
                      <Sparkles className="h-5 w-5 text-cyan-300" />
                      <div className="flex-1 text-sm text-cyan-100">
                        Welcome back! You have{" "}
                        <span className="font-mono text-cyan-300">{customerInfo.paid_bookings}</span>{" "}
                        prior booking{customerInfo.paid_bookings !== 1 && "s"}.
                        {loyaltyCredit > 0 && (
                          <span className="font-display font-bold text-emerald-300 ml-1">
                            $10 loyalty credit applied to this one!
                          </span>
                        )}
                        {loyaltyCredit === 0 && (
                          <span className="text-cyan-200/70 ml-1">
                            Loyalty $10 kicks in on booking #{Math.ceil((customerInfo.paid_bookings + 1) / 3) * 3}.
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {referralCode && refChecking && (
                    <div
                      className="mt-3 p-4 rounded-xl border border-white/10 bg-[#0f1522] flex items-center gap-3"
                      data-testid="referral-checking"
                    >
                      <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                      <span className="flex-1 text-sm text-slate-400">Checking code…</span>
                    </div>
                  )}

                  {referralCode && !refChecking && refStatus && (
                    <div
                      className={`mt-3 p-4 rounded-xl border flex items-center gap-3 ${
                        refStatus.valid
                          ? "bg-emerald-500/5 border-emerald-500/30"
                          : "bg-red-500/5 border-red-500/30"
                      }`}
                      data-testid="referral-indicator"
                    >
                      <Gift className={`h-5 w-5 ${refStatus.valid ? "text-emerald-400" : "text-red-400"}`} />
                      <div className="flex-1 text-sm">
                        {refStatus.valid ? (
                          <span className="text-emerald-200">
                            Referral code applied — you save <span className="font-display font-bold text-emerald-300">$10</span> on this booking.
                          </span>
                        ) : refStatus.reason === "not_new_customer" ? (
                          <span className="text-red-200">Referral codes are for first-time customers only.</span>
                        ) : refStatus.reason === "self" ? (
                          <span className="text-red-200">That's your own code — share it with a mate instead!</span>
                        ) : (
                          <span className="text-red-200">Code not recognised.</span>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="mt-4">
                    <Label>Notes (optional)</Label>
                    <textarea
                      data-testid="customer-notes-input"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Pet hair, bird dropping damage, access instructions…"
                      className="mt-2 w-full bg-[#0f1522] border border-white/10 rounded-xl px-4 py-3 text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-[#ff6b00] min-h-[90px]"
                    />
                  </div>
                </div>
              )}

              {stepIdx === 3 && (
                <div data-testid="step-schedule">
                  <h2 className="font-display uppercase text-2xl font-extrabold text-slate-100">Pick date & time</h2>
                  <div className="mt-6">
                    <Label>Date</Label>
                    <p className="mt-1 text-sm text-slate-400">Book any day that suits you — months ahead is fine.</p>
                    <div className="mt-3 inline-block rounded-2xl border border-white/10 bg-[#0f1522] p-2" data-testid="booking-calendar">
                      <Calendar
                        mode="single"
                        selected={dateSel ? isoToDate(dateSel) : undefined}
                        onSelect={(d) => {
                          if (!d) return;
                          setDateSel(dateToIso(d));
                          setTimeSel("");
                        }}
                        disabled={{ before: minDate }}
                        fromDate={minDate}
                        className="text-slate-200"
                        classNames={{
                          day_selected: "bg-[#ff6b00] text-[#0b0f17] hover:bg-[#ff6b00] hover:text-[#0b0f17] focus:bg-[#ff6b00]",
                          day_today: "text-[#ff6b00] font-bold",
                          caption_label: "text-sm font-semibold text-slate-100",
                        }}
                      />
                    </div>
                    {dateSel && (
                      <div className="mt-3 text-sm text-slate-400" data-testid="booking-date-selected">
                        Selected: <span className="font-display font-bold text-slate-100">{fmtDate(dateSel)}</span>
                      </div>
                    )}
                  </div>
                  <div className="mt-6">
                    <Label>Time slot</Label>
                    <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-2 min-h-[52px]">
                      {slotsLoading && (
                        <div className="col-span-full flex items-center gap-2 text-sm text-slate-400">
                          <Loader2 className="h-4 w-4 animate-spin" /> Loading slots…
                        </div>
                      )}
                      {!slotsLoading &&
                        slots.map((s) => (
                          <button
                            key={s.time}
                            disabled={!s.available}
                            type="button"
                            onClick={() => setTimeSel(s.time)}
                            data-testid={`booking-time-slot-${s.time.replace(":", "")}`}
                            className={`px-3 py-3 rounded-lg border font-mono font-semibold text-sm transition ${
                              timeSel === s.time
                                ? "bg-[#ff6b00] text-[#0b0f17] border-[#ff6b00]"
                                : s.available
                                ? "bg-[#0f1522] text-slate-200 border-white/10 hover:border-white/20"
                                : "bg-[#0a0e15] text-slate-600 border-white/5 line-through cursor-not-allowed"
                            }`}
                          >
                            {s.time}
                          </button>
                        ))}
                    </div>
                  </div>
                </div>
              )}

              {stepIdx === 4 && waitlisted && (
                <div data-testid="waitlist-confirmation" className="text-center py-6">
                  <div className="h-14 w-14 mx-auto rounded-full bg-purple-500/15 flex items-center justify-center">
                    <MapPin className="h-7 w-7 text-purple-300" />
                  </div>
                  <h2 className="font-display uppercase text-2xl font-extrabold text-slate-100 mt-4">You're on the waitlist</h2>
                  <p className="text-sm text-slate-400 mt-2 max-w-md mx-auto">
                    Your address is {waitlisted.distance_km} km out — beyond our usual range. No deposit was taken. CJ will review and email{" "}
                    <span className="text-slate-200">{waitlisted.customer.email}</span> with a yes/no shortly.
                  </p>
                  <div className="font-mono text-xs text-slate-500 mt-4">Ref · {waitlisted.id}</div>
                </div>
              )}

              {stepIdx === 4 && requested && (
                <div data-testid="request-confirmation" className="text-center py-6">
                  <div className="h-14 w-14 mx-auto rounded-full bg-emerald-500/15 flex items-center justify-center">
                    <Check className="h-7 w-7 text-emerald-300" />
                  </div>
                  <h2 className="font-display uppercase text-2xl font-extrabold text-slate-100 mt-4">Request received!</h2>
                  <p className="text-sm text-slate-400 mt-2 max-w-md mx-auto">
                    Thanks {requested.customer.name.split(" ")[0]} — CJ will review your request and email{" "}
                    <span className="text-slate-200">{requested.customer.email}</span> a secure link to pay your ${config.deposit_amount} deposit. Your booking is confirmed once the deposit is paid — nothing is charged now.
                  </p>
                  <div className="font-mono text-xs text-slate-500 mt-4">Ref · {requested.id}</div>
                </div>
              )}

              {stepIdx === 4 && !waitlisted && !requested && (
                <div data-testid="step-pay">
                  <h2 className="font-display uppercase text-2xl font-extrabold text-slate-100">
                    {beyondCap ? "Confirm & request waitlist" : "Review & request booking"}
                  </h2>
                  {beyondCap ? (
                    <p className="text-sm text-purple-200 mt-1" data-testid="waitlist-pay-notice">
                      You're outside our {areaCheck.max_travel_km} km range. We'll send your request to CJ — no deposit is charged unless it's approved.
                    </p>
                  ) : (
                    <p className="text-sm text-slate-400 mt-1">
                      Send your request to CJ. Once it's confirmed you'll get an email with a secure link to pay the ${config.deposit_amount} deposit — nothing is charged now.
                    </p>
                  )}

                  <div className="mt-6 rounded-xl border border-white/10 overflow-hidden">
                    <SumRow label="Service" value={`${service?.name || "—"}${service?.quote_only ? " (quote only)" : ""}`} />
                    <SumRow label="Vehicle" value={`${vehicle.year} ${vehicle.make} ${vehicle.model} · ${SIZES.find((z) => z.v === vehicle.size)?.l || vehicle.size}`} />
                    <SumRow label="Date" value={fmtDate(dateSel)} />
                    <SumRow label="Time" value={timeSel} />
                    <SumRow label="Address" value={customer.address} />
                    <SumRow label={service?.quote_only ? "Service price (from)" : "Service Price"} value={`$${servicePrice.toFixed(2)}`} />
                    {chosenAddons.map((a) => (
                      <SumRow key={a.id} label={`Add-on · ${a.name}`} value={`+$${priceFor(a, vehicle.size).toFixed(2)}`} />
                    ))}
                    {travelFee > 0 && <SumRow label="Travel surcharge" value={`+$${travelFee.toFixed(2)}`} />}
                    {loyaltyCredit > 0 && <SumRow label="Loyalty credit" value={`-$${loyaltyCredit.toFixed(2)}`} />}
                    {referralDiscount > 0 && <SumRow label="Referral credit" value={`-$${referralDiscount.toFixed(2)}`} />}
                    <SumRow label={beyondCap ? "Deposit (only if approved)" : "Deposit (after we confirm)"} value={`$${config.deposit_amount.toFixed(2)} ${config.currency}`} strong />
                    <SumRow label="Balance on completion" value={`$${balanceOnCompletion.toFixed(2)}`} />
                  </div>

                  <ExtrasToggles addons={addons} selected={selectedAddons} onToggle={toggleAddon} size={vehicle.size} />

                  <div className="mt-6" data-testid="recurring-opt-in">
                    <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">Keep it looking its best (optional)</div>
                    <p className="text-xs text-slate-500 mt-1">Start a maintenance plan — we'll email you before each visit to confirm and pay the deposit. Cancel anytime.</p>
                    <div className="mt-3 grid sm:grid-cols-2 gap-2">
                      {RECURRING_OPTS.map((o) => (
                        <button
                          type="button"
                          key={o.key}
                          onClick={() => setRecurringPlan(recurringPlan === o.key ? "" : o.key)}
                          data-testid={`recurring-choice-${o.key}`}
                          className={`text-left px-3 py-2.5 rounded-lg border text-sm transition ${recurringPlan === o.key ? "bg-[#ff6b00] text-[#0b0f17] border-[#ff6b00]" : "bg-[#0f1522] text-slate-300 border-white/10 hover:border-white/20"}`}
                        >
                          <div className="font-display uppercase font-bold text-xs tracking-wider">{o.label}</div>
                          <div className={`text-[10px] mt-0.5 ${recurringPlan === o.key ? "text-[#0b0f17]/70" : "text-slate-500"}`}>{o.cadence}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <label data-testid="terms-accept-box" className={`mt-6 p-4 rounded-xl border flex items-start gap-3 cursor-pointer transition ${termsAccepted ? "border-emerald-500/40 bg-emerald-500/5" : "border-white/10 bg-[#0f1522]"}`}>
                    <input
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      data-testid="terms-accept-checkbox"
                      className="mt-1 h-4 w-4 accent-[#ff6b00]"
                    />
                    <div className="text-sm text-slate-300">
                      I've read and accept the{" "}
                      <a href="/terms" target="_blank" rel="noopener noreferrer" data-testid="terms-link" className="text-[#ff6b00] underline">Terms &amp; Conditions</a>
                      {" "}— including the deposit refund policy (full refund 24 h+ before; deposit forfeit inside 24 h), the vehicle access &amp; condition requirements, and that final pricing may vary with vehicle condition.
                    </div>
                  </label>
                </div>
              )}

              {error && (
                <div data-testid="booking-error" className="mt-4 text-sm text-red-400 font-mono uppercase tracking-wider">
                  {error}
                </div>
              )}

              <div className="mt-8 flex items-center justify-between gap-3">
                {stepIdx > 0 && !waitlisted && !requested ? (
                  <button type="button" onClick={back} data-testid="step-back-button" className="cj-btn-ghost px-5 py-3 rounded-full inline-flex items-center gap-2">
                    <ChevronLeft className="h-4 w-4" /> Back
                  </button>
                ) : (
                  <div />
                )}
                {waitlisted || requested ? (
                  <button type="button" onClick={() => navigate("/")} data-testid="waitlist-home-button" className="cj-btn-primary px-6 py-3 rounded-full inline-flex items-center gap-2">
                    Back home <ChevronRight className="h-4 w-4" />
                  </button>
                ) : stepIdx < STEPS.length - 1 ? (
                  <button type="button" onClick={next} data-testid="step-next-button" className="cj-btn-primary px-6 py-3 rounded-full inline-flex items-center gap-2">
                    Continue <ChevronRight className="h-4 w-4" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={submitBooking}
                    disabled={submitting}
                    data-testid={beyondCap ? "waitlist-request-button" : "submit-booking-request-button"}
                    className="cj-btn-primary px-6 py-3 rounded-full inline-flex items-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                      </>
                    ) : beyondCap ? (
                      <>
                        Request waitlist spot <ChevronRight className="h-4 w-4" />
                      </>
                    ) : (
                      <>
                        Request booking <ChevronRight className="h-4 w-4" />
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* SUMMARY */}
          <div className="lg:col-span-1">
            <div className="cj-card p-6 sticky top-24">
              <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#ff6b00]">Live summary</div>
              <div className="mt-3 font-display uppercase font-extrabold text-2xl text-slate-100 leading-tight">
                {service?.name || "Pick a package"}
              </div>
              {service && (
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <span className="cj-pill bg-white/5 text-slate-300 border border-white/10">
                    <Clock className="h-3 w-3" /> {service.duration_minutes}m
                  </span>
                  <span className="font-mono text-xs text-slate-500">{service.quote_only ? "from" : SIZES.find((z) => z.v === vehicle.size)?.l}</span>
                  <span className="font-display font-extrabold text-xl text-[#ff6b00]" data-testid="summary-service-price">${servicePrice.toFixed(0)}</span>
                </div>
              )}
              <div className="my-5 h-px bg-white/5" />
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2 text-slate-300">
                  <Car className="h-4 w-4 text-slate-500" />
                  {vehicle.year || vehicle.make || vehicle.model ? `${vehicle.year} ${vehicle.make} ${vehicle.model}`.trim() : "Vehicle details"}
                </li>
                <li className="flex items-center gap-2 text-slate-300">
                  <CalendarDays className="h-4 w-4 text-slate-500" />
                  {dateSel ? fmtDate(dateSel) : "No date"} {timeSel && `· ${timeSel}`}
                </li>
                <li className="flex items-center gap-2 text-slate-300">
                  <MapPin className="h-4 w-4 text-slate-500" />
                  {customer.address || "Address"}
                </li>
              </ul>
              <div className="my-5 h-px bg-white/5" />
              <div className="space-y-1 text-sm">
                {chosenAddons.map((a) => (
                  <div key={a.id} className="flex justify-between text-cyan-200" data-testid={`summary-addon-${a.slug}`}>
                    <span>{a.name}</span>
                    <span className="font-mono">+${priceFor(a, vehicle.size).toFixed(2)}</span>
                  </div>
                ))}
                {travelFee > 0 && (
                  <div className="flex justify-between text-amber-300">
                    <span>Travel</span>
                    <span className="font-mono">+${travelFee.toFixed(2)}</span>
                  </div>
                )}
                {loyaltyCredit > 0 && (
                  <div className="flex justify-between text-emerald-300">
                    <span>Loyalty</span>
                    <span className="font-mono">-${loyaltyCredit.toFixed(2)}</span>
                  </div>
                )}
                {referralDiscount > 0 && (
                  <div className="flex justify-between text-emerald-300">
                    <span>Referral</span>
                    <span className="font-mono">-${referralDiscount.toFixed(2)}</span>
                  </div>
                )}
              </div>
              <div className="my-4 h-px bg-white/5" />
              <div className="flex items-center justify-between">
                <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">{beyondCap ? "Deposit if approved" : "Deposit after confirm"}</div>
                <div className="font-display font-extrabold text-2xl text-[#ff6b00]">${config.deposit_amount}</div>
              </div>
              <div className="flex items-center justify-between mt-1">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{service?.quote_only ? "Est. balance (quote)" : "Balance on completion"}</div>
                <div className="font-mono text-sm text-slate-300">${balanceOnCompletion.toFixed(2)}</div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}

function Label({ children }) {
  return <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">{children}</div>;
}

function Field({ label, value, onChange, placeholder, type = "text", testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="mt-2 w-full bg-[#0f1522] border border-white/10 rounded-xl px-4 py-3 text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-[#ff6b00]"
      />
    </div>
  );
}

function SumRow({ label, value, strong }) {
  return (
    <div className={`flex items-center justify-between gap-3 px-4 py-3 ${strong ? "bg-[#ff6b00]/10" : "bg-[#0f1522]"} border-b border-white/5 last:border-b-0`}>
      <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className={`text-sm text-right ${strong ? "font-display font-extrabold text-lg text-[#ff6b00]" : "text-slate-200"}`}>{value}</div>
    </div>
  );
}
